import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { signOut } from '../../../../services/auth';
import { navigateForAuth } from '../../../../services/authNavigation';
import {
  FRESH_SIGN_IN_PATH,
  SESSION_NOT_FRESH_MESSAGE,
} from '../../../../services/sessionFreshness';
import { hapticsImpactLight } from '../../../../utils/haptics';

interface FreshSignInPromptProps {
  /** `modal` inside a modal body. */
  context?: 'default' | 'modal';
  /** What the admin reads; defaults to the Security tab's wording. */
  message?: string;
  /** Where sign-in returns to afterwards; defaults to the Security tab. */
  returnTo?: string;
}

/**
 * Shown when the backend refused a change because the session is not fresh
 * (`SESSION_NOT_FRESH`: Better Auth's day-old limit on security changes, or
 * the admin passkey bootstrap's sign-in window): signing out and in again is
 * the only fix, and sign-in returns to `returnTo` (the Security tab by
 * default).
 */
const FreshSignInPrompt = ({
  context = 'default',
  message = SESSION_NOT_FRESH_MESSAGE,
  returnTo,
}: FreshSignInPromptProps) => {
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signInPath = returnTo
    ? `/auth?redirect=${encodeURIComponent(returnTo)}`
    : FRESH_SIGN_IN_PATH;

  const signOutAndReturn = async () => {
    hapticsImpactLight();
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      navigateForAuth(signInPath, { replace: true });
    }
  };

  return (
    <div className="flex flex-col items-start gap-2" role="alert">
      <p className="!my-0 text-sm text-status-danger">{message}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        context={context}
        disabled={isSigningOut}
        onClick={() => void signOutAndReturn()}
      >
        Sign Out
      </Button>
    </div>
  );
};

export default FreshSignInPrompt;
